import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, GraduationCap } from "lucide-react";
import { Reveal } from "./Reveal";

const PHOTO =
  "https://customer-assets.emergentagent.com/job_practical-borg-6/artifacts/f86u9ag1_WhatsApp%20Image%202026-06-02%20at%2014.46.44.jpeg";

const clientLogos = [
  {
    name: "Lune",
    src: "https://customer-assets.emergentagent.com/job_practical-borg-6/artifacts/pst9uq28_lune-logo-converted-webp.webp",
  },
  {
    name: "Opteam",
    src: "https://customer-assets.emergentagent.com/job_practical-borg-6/artifacts/x5vs6gf2_Opteam-logo-1024x208.png",
  },
  {
    name: "Baytukum",
    src: "https://customer-assets.emergentagent.com/job_practical-borg-6/artifacts/fthcp67y_baytukum_logo_gb_400.png",
  },
  {
    name: "Capital H",
    src: "https://customer-assets.emergentagent.com/job_practical-borg-6/artifacts/8p0fc6el_Color.png",
  },
  {
    name: "Ferrstron Powertrains",
    src: "https://customer-assets.emergentagent.com/job_practical-borg-6/artifacts/1x9edoyd_ferrstron_powertrains_private_limited_cover-2.jpeg",
  },
  {
    name: "Themis",
    src: "https://customer-assets.emergentagent.com/job_practical-borg-6/artifacts/4cy4ear8_Themis_logo.png",
  },
  {
    name: "BQPhy",
    src: "https://customer-assets.emergentagent.com/job_practical-borg-6/artifacts/baxa9wef_4d17a338a37d4136957e06b2c92b83e6.avif",
  },
];

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 border-t border-ink/15"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 lg:px-12">
        <Reveal direction="up">
          <div className="flex items-center gap-3 mb-10 md:mb-14">
            <span className="inline-block h-px w-8 bg-ink/40" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/60">
              About Us
            </span>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: big headline */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Reveal direction="up" delay={0.05}>
              <h2 className="font-display font-black text-[clamp(2.5rem,6.5vw,5.5rem)] leading-[0.96] tracking-[-0.025em] text-ink">
                An operator who&rsquo;s{" "}
                <span className="text-money italic font-medium">sat on both sides</span> of the
                table.
              </h2>
            </Reveal>

            <div className="mt-10 md:mt-14 space-y-6 max-w-[64ch]">
              <Reveal direction="up" delay={0.15}>
                <p className="font-sans-pro text-base md:text-lg leading-relaxed text-ink/75">
                  With experience on both the investor and founder side, Divyat combines
                  hands-on financial expertise with a trusted network across{" "}
                  <span className="text-money font-medium">venture capital</span>, family
                  offices, startup operators, and institutional investors.
                </p>
              </Reveal>
              <Reveal direction="up" delay={0.25}>
                <p className="font-sans-pro text-base md:text-lg leading-relaxed text-ink/75">
                  RCS Labs operates as the startup-focused offshoot of{" "}
                  <a
                    href="https://rcsadvisors.in/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="about-rcs-advisors-link"
                    className="font-bold text-ink underline underline-offset-4 decoration-money/60 hover:decoration-money hover:bg-acid transition-colors"
                  >
                    RCS Advisors
                  </a>
                  , bringing together Divyat&rsquo;s venture and startup operating experience with RCS Advisors&rsquo;{" "}
                  <span className="text-money font-medium">25+ years</span> of M&amp;A, corporate
                  finance, and industry relationships.
                </p>
              </Reveal>
            </div>
          </div>

          {/* Right: profile card */}
          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-28">
            <Reveal direction="up" delay={0.1}>
              <div
                data-testid="about-profile-card"
                className="bg-white border border-ink/15 rounded-2xl p-6 md:p-7 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)]"
              >
                {/* Top row: photo + name + available badge */}
                <div className="flex items-start gap-4">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="relative shrink-0"
                  >
                    <div className="relative h-16 w-16 md:h-20 md:w-20 rounded-full p-[3px] bg-gradient-to-br from-money to-emerald2">
                      <div className="h-full w-full rounded-full overflow-hidden border-2 border-white">
                        <img
                          src={PHOTO}
                          alt="Divyat Rungta"
                          data-testid="about-portrait"
                          className="h-full w-full object-cover"
                          style={{ transform: "scale(1.2)", transformOrigin: "center 30%" }}
                        />
                      </div>
                    </div>
                  </motion.div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-xl md:text-2xl text-ink leading-tight">
                      Divyat Rungta
                    </h3>
                    <p className="font-sans-pro text-sm text-ink/60 mt-0.5">
                      Founder, RCS Labs
                    </p>
                  </div>

                  <span
                    data-testid="about-available-badge"
                    className="shrink-0 inline-flex items-center gap-1.5 bg-money/10 border border-money/30 px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.15em] text-money"
                  >
                    <motion.span
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                      className="h-1.5 w-1.5 rounded-full bg-money"
                    />
                    Available
                  </span>
                </div>

                {/* Client logos — companies Divyat has worked with */}
                <div className="mt-6" data-testid="about-client-logos">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45 mb-3">
                    Worked with
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-3 gap-y-4">
                    {clientLogos.map((c, i) => (
                      <motion.div
                        key={`${c.name}-${i}`}
                        initial={{ opacity: 0, y: 6 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{
                          duration: 0.5,
                          delay: 0.1 + i * 0.05,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                        whileHover={{ y: -2 }}
                        className="h-14 flex items-center justify-center"
                        title={c.name}
                      >
                        <img
                          src={c.src}
                          alt={c.name}
                          className="max-h-full max-w-full object-contain"
                          loading="lazy"
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Footer meta */}
                <div className="mt-6 pt-5 border-t border-ink/10 flex flex-wrap items-center gap-x-5 gap-y-2 text-ink/70">
                  <span className="inline-flex items-center gap-1.5 font-sans-pro text-sm">
                    <MapPin className="w-3.5 h-3.5 text-money" strokeWidth={2} />
                    India &amp; UAE
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-sans-pro text-sm">
                    <GraduationCap className="w-3.5 h-3.5 text-money" strokeWidth={2} />
                    Cambridge MFin · SRCC Economics
                  </span>
                </div>

                {/* CTA */}
                <a
                  href="https://www.linkedin.com/in/divyat-rungta-6441333a/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-linkedin"
                  className="group mt-6 inline-flex w-full items-center justify-center gap-2 bg-ink text-cream px-5 py-3 rounded-lg font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-money transition-colors duration-300"
                >
                  Connect on LinkedIn
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
